import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  Eye,
  Calendar,
  MapPin,
  User,
  Phone,
  CreditCard,
  Shield,
  Building2,
  FileText,
  Camera,
  CheckCircle,
  X,
  Globe,
  Hash,
  ArrowUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthenticatedNavbar from "../components/AuthenticatedNavbar";

const PayoutSetup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showTermsModal, setShowTermsModal] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    accountHolderName: "",
    contactPhoneNumber: "",
    dateOfBirth: "",
    countryOfResidence: "Ghana",
    primaryAddress: "",
    taxIdentificationNumber: "",

    // Step 2: Identity Verification
    identificationType: "",
    identificationNumber: "",
    issueDate: "",
    expiryDate: "",
    idFrontImage: null,
    idBackImage: null,

    // Step 3: Account Setup
    networkType: "",
    accountNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      if (formData.idFrontImagePreview) {
        URL.revokeObjectURL(formData.idFrontImagePreview);
      }
      if (formData.idBackImagePreview) {
        URL.revokeObjectURL(formData.idBackImagePreview);
      }
    };
  }, [formData.idFrontImagePreview, formData.idBackImagePreview]);

  const steps = [
    { id: 0, title: "Personal Information", icon: User },
    { id: 1, title: "Identity Verification", icon: Shield },
    { id: 2, title: "Account Setup", icon: CreditCard },
    { id: 3, title: "Review & Submit", icon: CheckCircle },
  ];

  const identificationTypes = [
    { value: "ghana_card", label: "Ghana Card", icon: CreditCard },
    { value: "passport", label: "Passport", icon: FileText },
    { value: "voter_card", label: "Voter's Card", icon: FileText },
  ];

  const networks = [
    {
      value: "mtn",
      label: "MTN",
      color: "bg-yellow-500",
      placeholder: "MTN Logo",
    },
    {
      value: "telecel",
      label: "Telecel",
      color: "bg-red-500",
      placeholder: "Telecel Logo",
    },
    {
      value: "airteltigo",
      label: "AirtelTigo",
      color: "bg-blue-500",
      placeholder: "AirtelTigo Logo",
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (field, file) => {
    console.log("File upload triggered:", { field, file });

    if (file && file.type.startsWith("image/")) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      console.log("Preview URL created:", previewUrl);

      setFormData((prev) => {
        const newData = {
          ...prev,
          [field]: file,
          [`${field}Preview`]: previewUrl,
        };
        console.log("Updated form data:", newData);
        return newData;
      });
      setErrors((prev) => ({ ...prev, [field]: "" }));
      toast.success("Image uploaded successfully!");
    } else {
      toast.error("Please upload a valid image file");
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.accountHolderName.trim())
          newErrors.accountHolderName = "Account holder name is required";
        if (!formData.contactPhoneNumber.trim())
          newErrors.contactPhoneNumber = "Contact phone number is required";
        if (!formData.dateOfBirth)
          newErrors.dateOfBirth = "Date of birth is required";
        if (!formData.countryOfResidence.trim())
          newErrors.countryOfResidence = "Country of residence is required";
        if (!formData.primaryAddress.trim())
          newErrors.primaryAddress = "Primary address is required";
        if (!formData.taxIdentificationNumber.trim())
          newErrors.taxIdentificationNumber =
            "Tax identification number is required";
        break;
      case 1:
        if (!formData.identificationType)
          newErrors.identificationType = "Identification type is required";
        if (!formData.identificationNumber.trim())
          newErrors.identificationNumber = "Identification number is required";
        if (!formData.issueDate) newErrors.issueDate = "Issue date is required";
        if (!formData.expiryDate)
          newErrors.expiryDate = "Expiry date is required";
        if (!formData.idFrontImage)
          newErrors.idFrontImage = "Front image is required";
        if (!formData.idBackImage)
          newErrors.idBackImage = "Back image is required";
        break;
      case 2:
        if (!formData.networkType)
          newErrors.networkType = "Network type is required";
        if (!formData.accountNumber.trim())
          newErrors.accountNumber = "Account number is required";
        break;
      default:
        return null;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      toast.success("Payout account setup completed successfully!");
      navigate("/earnings");
    } catch (error) {
      toast.error("Failed to setup payout account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const FileUploadPreview = ({
    file,
    onRemove,
    label,
    error,
    previewUrl,
    fieldName,
  }) => {
    const inputId = `file-input-${fieldName}`;

    const handleClick = () => {
      const input = document.getElementById(inputId);
      if (input) {
        input.click();
      }
    };

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {file ? (
          <div className="relative">
            {/* Image Preview */}
            <div className="mb-4">
              <div className="overflow-hidden relative bg-gray-50 rounded-xl border border-gray-200">
                <img
                  src={previewUrl}
                  alt="Upload preview"
                  className="object-cover w-full h-48"
                />
                <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 transition-all duration-200 hover:bg-opacity-10">
                  <div className="opacity-0 transition-opacity duration-200 hover:opacity-100">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* File Info */}
            <div className="flex gap-3 items-center p-4 bg-gray-50 rounded-xl border border-gray-300">
              <div className="flex justify-center items-center w-12 h-12 bg-white rounded-lg border border-gray-200">
                <Camera className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={onRemove}
                className="p-2 text-gray-400 transition-colors hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Change File Button */}
            <button
              onClick={handleClick}
              className="mt-2 w-full px-4 py-2 text-sm text-[#F2640F] border border-[#F2640F] rounded-lg hover:bg-[#F2640F]/5 transition-colors"
            >
              Change File
            </button>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(fieldName, e.target.files[0])}
              className="hidden"
            />
          </div>
        ) : (
          <div className="relative">
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#F2640F] transition-colors cursor-pointer"
              onClick={handleClick}
            >
              <Upload className="mx-auto mb-2 w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
            </div>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(fieldName, e.target.files[0])}
              className="hidden"
            />
          </div>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Account Holder's Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    value={formData.accountHolderName}
                    onChange={(e) =>
                      handleInputChange("accountHolderName", e.target.value)
                    }
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                      errors.accountHolderName
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter full name"
                  />
                </div>
                {errors.accountHolderName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.accountHolderName}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Contact Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="tel"
                    value={formData.contactPhoneNumber}
                    onChange={(e) =>
                      handleInputChange("contactPhoneNumber", e.target.value)
                    }
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                      errors.contactPhoneNumber
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="+233 20 123 4567"
                  />
                </div>
                {errors.contactPhoneNumber && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.contactPhoneNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Date of Birth *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) =>
                      handleInputChange("dateOfBirth", e.target.value)
                    }
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                      errors.dateOfBirth ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Country of Residence *
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <select
                    value={formData.countryOfResidence}
                    onChange={(e) =>
                      handleInputChange("countryOfResidence", e.target.value)
                    }
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                      errors.countryOfResidence
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="Ghana">Ghana</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Kenya">Kenya</option>
                    <option value="South Africa">South Africa</option>
                  </select>
                </div>
                {errors.countryOfResidence && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.countryOfResidence}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Primary Address *
              </label>
              <div className="relative">
                <MapPin className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <textarea
                  value={formData.primaryAddress}
                  onChange={(e) =>
                    handleInputChange("primaryAddress", e.target.value)
                  }
                  rows={3}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                    errors.primaryAddress ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter your complete address"
                />
              </div>
              {errors.primaryAddress && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.primaryAddress}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Tax Identification Number *
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={formData.taxIdentificationNumber}
                  onChange={(e) =>
                    handleInputChange("taxIdentificationNumber", e.target.value)
                  }
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                    errors.taxIdentificationNumber
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter your TIN"
                />
              </div>
              {errors.taxIdentificationNumber && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.taxIdentificationNumber}
                </p>
              )}
            </div>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Identification Type *
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {identificationTypes.map((type) => (
                    <label
                      key={type.value}
                      className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                        formData.identificationType === type.value
                          ? "border-[#F2640F] bg-[#F2640F]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="identificationType"
                        value={type.value}
                        checked={formData.identificationType === type.value}
                        onChange={(e) =>
                          handleInputChange(
                            "identificationType",
                            e.target.value
                          )
                        }
                        className="sr-only"
                      />
                      <div className="flex gap-3 items-center">
                        <type.icon className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-900">
                          {type.label}
                        </span>
                      </div>
                      {formData.identificationType === type.value && (
                        <div className="absolute top-2 right-2 w-4 h-4 bg-[#F2640F] rounded-full flex items-center justify-center">
                          <Check className="w-2 h-2 text-white" />
                        </div>
                      )}
                    </label>
                  ))}
                </div>
                {errors.identificationType && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.identificationType}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Identification Number *
                </label>
                <input
                  type="text"
                  value={formData.identificationNumber}
                  onChange={(e) =>
                    handleInputChange("identificationNumber", e.target.value)
                  }
                  disabled={!formData.identificationType}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                    errors.identificationNumber
                      ? "border-red-300"
                      : "border-gray-300"
                  } ${
                    !formData.identificationType
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                  }`}
                  placeholder={
                    formData.identificationType
                      ? "Enter identification number"
                      : "Select identification type first"
                  }
                />
                {errors.identificationNumber && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.identificationNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Issue Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) =>
                      handleInputChange("issueDate", e.target.value)
                    }
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                      errors.issueDate ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.issueDate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.issueDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Expiry Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      handleInputChange("expiryDate", e.target.value)
                    }
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                      errors.expiryDate ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.expiryDate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.expiryDate}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FileUploadPreview
                file={formData.idFrontImage}
                onRemove={() => {
                  if (formData.idFrontImagePreview) {
                    URL.revokeObjectURL(formData.idFrontImagePreview);
                  }
                  handleInputChange("idFrontImage", null);
                  handleInputChange("idFrontImagePreview", null);
                }}
                label="ID Front Image"
                error={errors.idFrontImage}
                previewUrl={formData.idFrontImagePreview}
                fieldName="idFrontImage"
              />

              <FileUploadPreview
                file={formData.idBackImage}
                onRemove={() => {
                  if (formData.idBackImagePreview) {
                    URL.revokeObjectURL(formData.idBackImagePreview);
                  }
                  handleInputChange("idBackImage", null);
                  handleInputChange("idBackImagePreview", null);
                }}
                label="ID Back Image"
                error={errors.idBackImage}
                previewUrl={formData.idBackImagePreview}
                fieldName="idBackImage"
              />
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <label className="block mb-4 text-sm font-medium text-gray-700">
                Select Mobile Money Network *
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {networks.map((network) => (
                  <label
                    key={network.value}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 ${
                      formData.networkType === network.value
                        ? "border-[#F2640F] bg-[#F2640F]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="networkType"
                      value={network.value}
                      checked={formData.networkType === network.value}
                      onChange={(e) =>
                        handleInputChange("networkType", e.target.value)
                      }
                      className="sr-only"
                    />
                    <div className="text-center">
                      <div
                        className={`w-16 h-16 mx-auto mb-3 rounded-xl ${network.color} flex items-center justify-center text-white font-bold text-lg`}
                      >
                        {network.placeholder}
                      </div>
                      <p className="font-medium text-gray-900">
                        {network.label}
                      </p>
                    </div>
                    {formData.networkType === network.value && (
                      <div className="absolute top-2 right-2 w-4 h-4 bg-[#F2640F] rounded-full flex items-center justify-center">
                        <Check className="w-2 h-2 text-white" />
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {errors.networkType && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.networkType}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Account Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="tel"
                  value={formData.accountNumber}
                  onChange={(e) =>
                    handleInputChange("accountNumber", e.target.value)
                  }
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#F2640F] focus:border-transparent ${
                    errors.accountNumber ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter mobile money number"
                />
              </div>
              {errors.accountNumber && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.accountNumber}
                </p>
              )}
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            {/* Header */}
            <div className="text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-2xl">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                Review Your Information
              </h3>
              <p className="text-gray-600">
                Please verify all details before submitting your payout setup
              </p>
            </div>

            {/* Review Cards */}
            <div className="space-y-6">
              {/* Personal Information Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex gap-3 items-center mb-6">
                  <div className="flex justify-center items-center w-12 h-12 bg-blue-50 rounded-xl">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Personal Information
                    </h4>
                    <p className="text-sm text-gray-500">
                      Your personal details and contact information
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Full Name
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.accountHolderName}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Phone Number
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.contactPhoneNumber}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Date of Birth
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.dateOfBirth}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Country
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.countryOfResidence}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 sm:col-span-2 lg:col-span-1">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Tax ID
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.taxIdentificationNumber}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Address
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.primaryAddress}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Identity Verification Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex gap-3 items-center mb-6">
                  <div className="flex justify-center items-center w-12 h-12 bg-green-50 rounded-xl">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Identity Verification
                    </h4>
                    <p className="text-sm text-gray-500">
                      Your identification documents and details
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        ID Type
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {
                          identificationTypes.find(
                            (t) => t.value === formData.identificationType
                          )?.label
                        }
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        ID Number
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.identificationNumber}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Issue Date
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.issueDate}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Expiry Date
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formData.expiryDate}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 sm:col-span-2 lg:col-span-1">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Front Image
                      </p>
                      <div className="flex gap-2 items-center mt-1">
                        {formData.idFrontImage ? (
                          <>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <p className="text-sm font-semibold text-green-600">
                              Uploaded
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <p className="text-sm font-semibold text-red-600">
                              Not uploaded
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Back Image
                      </p>
                      <div className="flex gap-2 items-center mt-1">
                        {formData.idBackImage ? (
                          <>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <p className="text-sm font-semibold text-green-600">
                              Uploaded
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <p className="text-sm font-semibold text-red-600">
                              Not uploaded
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Account Setup Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex gap-3 items-center mb-6">
                  <div className="flex justify-center items-center w-12 h-12 bg-purple-50 rounded-xl">
                    <CreditCard className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Account Setup
                    </h4>
                    <p className="text-sm text-gray-500">
                      Your mobile money payout account details
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                    <p className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
                      Mobile Money Network
                    </p>
                    <div className="flex gap-3 items-center">
                      <div
                        className={`w-12 h-12 rounded-xl ${
                          networks.find((n) => n.value === formData.networkType)
                            ?.color
                        } flex items-center justify-center text-white font-bold text-lg`}
                      >
                        {
                          networks.find((n) => n.value === formData.networkType)
                            ?.placeholder
                        }
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {
                            networks.find(
                              (n) => n.value === formData.networkType
                            )?.label
                          }
                        </p>
                        <p className="text-xs text-gray-500">Mobile Money</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                    <p className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
                      Account Number
                    </p>
                    <div className="flex gap-3 items-center">
                      <div className="flex justify-center items-center w-12 h-12 bg-white rounded-xl border border-gray-200">
                        <Phone className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {formData.accountNumber}
                        </p>
                        <p className="text-xs text-gray-500">Mobile Number</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Summary Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 bg-gradient-to-r from-[#F2640F]/5 to-[#FF0000]/5 rounded-2xl border border-[#F2640F]/20"
            >
              <div className="flex gap-3 items-center mb-4">
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-xl">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">
                    Ready to Submit
                  </h4>
                  <p className="text-sm text-gray-600">
                    All required information has been provided
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="flex gap-2 items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-gray-600">
                    Personal Information Complete
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-gray-600">
                    Identity Documents Uploaded
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-gray-600">
                    Account Details Verified
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
      <AuthenticatedNavbar />

      {/* Terms Modal */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="p-8 mx-4 max-w-md text-center bg-white rounded-2xl"
            >
              {/* Header */}
              <div className="mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-[#F2640F] mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <h2 className="mb-2 text-2xl font-bold text-gray-900">
                  Payout Account Set-up
                </h2>
                <div className="w-full h-1 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-full"></div>
              </div>

              {/* Illustration */}
              <div className="mb-6">
                <div className="flex relative justify-center items-center mx-auto w-32 h-32 bg-teal-100 rounded-full">
                  <div className="flex justify-center items-center w-16 h-16 text-2xl font-bold text-white bg-yellow-500 rounded-full">
                    $
                  </div>
                  <div className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 bg-green-500 rounded-full">
                    <ArrowUpRight className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="mb-6 text-gray-600">
                To receive your earnings from advertisers, please set up your
                preferred payout account. This ensures timely and secure
                transfer of funds to your linked payment method.
              </p>

              {/* Terms Checkbox */}
              <div className="flex gap-3 items-center mb-6">
                <button
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    acceptedTerms
                      ? "bg-[#F2640F] border-[#F2640F]"
                      : "border-gray-300"
                  }`}
                >
                  {acceptedTerms && <Check className="w-3 h-3 text-white" />}
                </button>
                <span className="text-sm text-gray-600">
                  I have read and accept the{" "}
                  <span className="text-[#F2640F] cursor-pointer hover:underline">
                    Terms & Conditions
                  </span>
                </span>
              </div>

              {/* Action Button */}
              <button
                onClick={() => {
                  if (acceptedTerms) {
                    setShowTermsModal(false);
                  } else {
                    toast.error("Please accept the terms and conditions");
                  }
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Get Started
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      {!showTermsModal && (
        <div className="px-4 py-16 mx-auto max-w-6xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-[#F2640F] transition-all duration-300 rounded-xl hover:bg-white/80 hover:shadow-md group"
              >
                <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
                <span className="font-semibold">Back</span>
              </button>

              <div className="flex gap-3 items-center">
                <div className="p-2 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-xl">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-600">
                    Step {currentStep + 1} of {steps.length}
                  </p>
                  <p className="text-xs text-gray-500">
                    {steps[currentStep].title}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 justify-center items-center mb-4">
                <div className="p-3 bg-gradient-to-r from-[#F2640F]/10 to-[#FF0000]/10 rounded-xl">
                  <Building2 className="w-8 h-8 text-[#F2640F]" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Payout Account Setup
                  </h1>
                  <p className="mt-1 text-gray-600">
                    Complete your payout account setup in a few simple steps
                  </p>
                </div>
              </div>

              <div className="mx-auto w-full max-w-md">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    Progress
                  </span>
                  <span className="text-sm font-medium text-[#F2640F]">
                    {Math.round(((currentStep + 1) / steps.length) * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <motion.div
                    className="bg-gradient-to-r from-[#F2640F] to-[#FF0000] h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${((currentStep + 1) / steps.length) * 100}%`,
                    }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        currentStep >= step.id
                          ? "bg-[#F2640F] text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {currentStep > step.id ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="hidden ml-3 sm:block">
                      <p
                        className={`text-sm font-medium ${
                          currentStep >= step.id
                            ? "text-[#F2640F]"
                            : "text-gray-500"
                        }`}
                      >
                        {step.title}
                      </p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-16 h-1 mx-4 transition-all duration-300 ${
                        currentStep > step.id ? "bg-[#F2640F]" : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-8 bg-white rounded-2xl border border-gray-100 shadow-lg"
          >
            {renderStepContent()}
          </motion.div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                currentStep === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </button>

            {currentStep < steps.length - 1 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Complete Setup
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutSetup;
